import { Tag, Tooltip } from 'antd';
import {
  AlertOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  HistoryOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
  WarningFilled,
} from '@ant-design/icons';
import type { PriorDispositionRef, WorkItemPriority, WorkItemStatus, WorkItemType } from '@/api/types';
import { formatAbsolute, formatDay, formatRelative, slaState } from '@/lib/time';
import {
  INTENT_TAG_COLOR,
  INTENT_VAR,
  PRIORITY_INTENT,
  STATUS_INTENT,
  WORK_ITEM_TYPE_LABEL,
} from './meta';
import { erasedOr } from '@/lib/erased';

/** The per-type glyph. Colour is carried by the surrounding label, never by the glyph alone. */
export function WorkItemTypeIcon({ type }: { type: WorkItemType }) {
  switch (type) {
    case 'HUMAN_TASK':
      return <UserOutlined aria-hidden />;
    case 'APPROVAL':
      return <SafetyCertificateOutlined aria-hidden />;
    case 'SIGNAL_REVIEW':
      return <AlertOutlined aria-hidden />;
    case 'RFI_FOLLOWUP':
      return <MailOutlined aria-hidden />;
    default:
      return <AlertOutlined aria-hidden />;
  }
}

/** Type glyph + label — the identity of a queue row. */
export function WorkItemTypeBadge({ type }: { type: WorkItemType }) {
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, whiteSpace: 'nowrap' }}
      title={WORK_ITEM_TYPE_LABEL[type] ?? type}
    >
      <WorkItemTypeIcon type={type} />
      {WORK_ITEM_TYPE_LABEL[type] ?? type}
    </span>
  );
}

/** Priority with a shape-coded dot (HIGH is bold + warm). */
export function PriorityBadge({ priority }: { priority: WorkItemPriority }) {
  const intent = PRIORITY_INTENT[priority] ?? 'neutral';
  return (
    <span className="ol-priority" style={{ color: INTENT_VAR[intent] }} title={`Priority ${priority}`}>
      <span className="ol-priority__dot" style={{ background: INTENT_VAR[intent] }} aria-hidden />
      {priority}
    </span>
  );
}

/** Work-item status tag (colour + verbatim token). */
export function WorkItemStatusTag({ status }: { status: WorkItemStatus }) {
  const intent = STATUS_INTENT[status] ?? 'neutral';
  return (
    <Tag bordered={false} color={INTENT_TAG_COLOR[intent]}>
      {status}
    </Tag>
  );
}

/**
 * SLA badge — relative "due in 2h" (overdue = red + warning glyph; at-risk = amber clock).
 * Pass a shared `now` so every row in a render measures from the same instant.
 */
export function SlaBadge({
  slaDueAt,
  createdAt,
  now,
}: {
  slaDueAt?: string | null;
  createdAt?: string | null;
  now?: number;
}) {
  const s = slaState(slaDueAt, createdAt, now);
  if (s.level === 'none') {
    return (
      <span className="ol-sla" aria-label="No SLA">
        —
      </span>
    );
  }
  const icon =
    s.level === 'overdue' ? <WarningFilled aria-hidden /> : <ClockCircleOutlined aria-hidden />;
  return (
    <Tooltip title={`Due ${formatAbsolute(slaDueAt)}`}>
      <span className={`ol-sla ol-sla--${s.level}`} aria-label={`SLA ${s.label}`}>
        <span className="ol-sla__pill">
          {icon}
          {s.label}
        </span>
      </span>
    </Tooltip>
  );
}

/** Age — relative time since creation, for the queue's "how long has this waited" column. */
export function AgeBadge({ createdAt, now }: { createdAt?: string | null; now?: number }) {
  return (
    <Tooltip title={formatAbsolute(createdAt)}>
      <span style={{ fontSize: 11.5, color: 'var(--ol-text-tertiary)', whiteSpace: 'nowrap' }}>
        {formatRelative(createdAt, now)}
      </span>
    </Tooltip>
  );
}

/**
 * Prior-disposition memory badge (F-IS-03). `sameFingerprint` = this signal matches an already
 * cleared one → the "stays cleared" affordance, rendered green with a check.
 */
export function MemoryBadge({ prior }: { prior: PriorDispositionRef }) {
  const same = Boolean(prior.sameFingerprint);
  const escalated = prior.disposition?.toUpperCase().includes('ESCAL');
  const cls = same
    ? 'ol-memory ol-memory--same'
    : escalated
      ? 'ol-memory ol-memory--escalated'
      : 'ol-memory';
  const tip = [
    `${prior.disposition} ${formatDay(prior.at)}`,
    prior.actor ? `by ${prior.actor}` : null,
    same ? 'same fingerprint as this signal' : null,
    prior.linkedEntityId ? `linked ${prior.linkedEntityId}` : null,
    prior.note ? `“${erasedOr(prior.note)}”` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Tooltip title={tip}>
      <span className={cls}>
        {same ? <CheckCircleFilled aria-hidden /> : <HistoryOutlined aria-hidden />}
        {prior.disposition} {formatDay(prior.at)}
        {prior.actor ? ` · ${prior.actor}` : ''}
        {same ? ' · same fingerprint' : ''}
      </span>
    </Tooltip>
  );
}
